import {
  Alert,
  Button,
  Group,
  Input,
  MultiSelect,
  type MultiSelectOption,
  Select,
  Stack,
  Textarea,
  Title,
} from "@assetwatch/ui";
import type { AssetId } from "../lib/workIds";
import type { ProcedureSummary } from "../queries/useProceduresQuery";
import type { TeamsUsersResponse } from "../queries/useTeamsUsersQuery";
import type { WorkOrderLookupsResponse } from "../queries/useWorkOrderLookupsQuery";
import type { WorkRequestApproval } from "../types/workRequestApi";
import { AssetSelector } from "./snowflakes/requests/AssetSelector";
import { EquipmentLocationPicker } from "./snowflakes/requests/EquipmentLocationPicker";

type Props = {
  approval: Pick<WorkRequestApproval, "asset" | "title" | "description">;
  lookups?: WorkOrderLookupsResponse;
  teamsUsers?: TeamsUsersResponse;
  procedures?: Array<Pick<ProcedureSummary, "id" | "name" | "status">>;
  procedureStatus?: "loading" | "error" | "ready";
  onRetryProcedures?: () => void;
  resetLabel?: string;
  readonlyDetails?: boolean;
  procedureDisabled?: boolean;
  selectedAssetTreeId?: number | null;
  assetRequired?: boolean;
  selectedAssetId?: AssetId | null;
  onPublicAssetChange?: (id: AssetId | null) => void;
  showVendor?: boolean;
  onAssetChange?: (
    id: number | null,
    name: string | null,
    publicId: AssetId | null,
  ) => void;
  title: string;
  description: string;
  priority: string;
  workType: string;
  assignees: string[];
  estimatedHours: string;
  startDate: string;
  dueDate: string;
  procedure: string;
  vendor: string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onWorkTypeChange: (value: string) => void;
  onAssigneesChange: (value: string[]) => void;
  onEstimatedHoursChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onDueDateChange: (value: string) => void;
  onProcedureChange: (value: string) => void;
  onVendorChange: (value: string) => void;
  onReset: () => void;
};
export function ApproveWorkOrderForm({
  approval,
  lookups,
  teamsUsers,
  procedures = [],
  procedureStatus = "ready",
  onRetryProcedures,
  resetLabel = "Reset from request",
  readonlyDetails = false,
  procedureDisabled = false,
  selectedAssetTreeId,
  assetRequired = true,
  selectedAssetId = null,
  onPublicAssetChange,
  showVendor = true,
  onAssetChange,
  title,
  description,
  priority,
  workType,
  assignees,
  estimatedHours,
  startDate,
  dueDate,
  procedure,
  vendor,
  onTitleChange,
  onDescriptionChange,
  onPriorityChange,
  onWorkTypeChange,
  onAssigneesChange,
  onEstimatedHoursChange,
  onStartDateChange,
  onDueDateChange,
  onProcedureChange,
  onVendorChange,
  onReset,
}: Props) {
  // Same `user:<id>` / `team:<id>` token convention as the Work Order edit
  // screen's MultiSelect (WorkOrderSplitView.tsx), so a single control can
  // assign any combination of people and teams at approval time.
  const assigneeOptions: MultiSelectOption[] = [
    ...(teamsUsers?.users ?? []).map((user) => ({
      value: `user:${user.id}`,
      label: user.name,
    })),
    ...(teamsUsers?.teams ?? [])
      .filter((team) => team.isActive)
      .map((team) => ({
        value: `team:${team.id}`,
        label: `${team.name} (team)`,
      })),
  ];
  return (
    <Stack gap={12}>
      <Group justify="between">
        <Title as="h3" size="sm">
          Work Order Details
        </Title>
        <Button
          disabled={readonlyDetails}
          size="sm"
          variant="ghost"
          onClick={onReset}
        >
          {resetLabel}
        </Button>
      </Group>
      <Input
        label="Title"
        disabled={readonlyDetails}
        required
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
      />
      <Textarea
        label="Description"
        disabled={readonlyDetails}
        required
        value={description}
        onChange={(event) => onDescriptionChange(event.target.value)}
      />
      <div className="grid grid-cols-1 gap-(--spacing-12) md:grid-cols-2">
        <Select
          disabled={readonlyDetails}
          label="Priority"
          onChange={(value) => onPriorityChange(value ?? "MEDIUM")}
          options={
            lookups?.priorities.map((item) => ({
              label: item.name,
              value: item.code,
            })) ?? [
              { label: "Low", value: "LOW" },
              { label: "Medium", value: "MEDIUM" },
              { label: "High", value: "HIGH" },
            ]
          }
          value={priority}
        />
        <Select
          disabled={readonlyDetails}
          label="Work type"
          onChange={(value) => onWorkTypeChange(value ?? "REACTIVE")}
          options={
            lookups?.workOrderTypes.map((item) => ({
              label: item.name,
              value: item.code,
            })) ?? [{ label: "Reactive", value: "REACTIVE" }]
          }
          value={workType}
        />
        <Input
          label="Location"
          readOnly
          value={approval.asset.locationName ?? ""}
        />
      </div>
      {onPublicAssetChange && !readonlyDetails ? (
        <EquipmentLocationPicker
          label="Asset"
          value={selectedAssetId}
          onChange={onPublicAssetChange}
        />
      ) : onAssetChange && !readonlyDetails ? (
        <AssetSelector
          value={selectedAssetTreeId ?? null}
          onChange={onAssetChange}
          required={assetRequired}
        />
      ) : (
        <Input label="Asset" value={approval.asset.name} readOnly />
      )}
      <div className="grid grid-cols-1 gap-(--spacing-12) md:grid-cols-2">
        <div className="md:col-span-2">
          <MultiSelect
            disabled={readonlyDetails}
            label="Assign to"
            onChange={onAssigneesChange}
            options={assigneeOptions}
            placeholder="Unassigned"
            value={assignees}
          />
        </div>
        <Input
          disabled={readonlyDetails}
          label="Estimated hours"
          min="0"
          placeholder="e.g. 2.5"
          step="0.25"
          type="number"
          value={estimatedHours}
          onChange={(event) => onEstimatedHoursChange(event.target.value)}
        />
        <Input
          disabled={readonlyDetails}
          label="Start date"
          type="date"
          value={startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
        />
        <Input
          disabled={readonlyDetails}
          label="Due date"
          type="date"
          value={dueDate}
          onChange={(event) => onDueDateChange(event.target.value)}
        />
      </div>
      <Select
        searchable
        disabled={readonlyDetails || procedureDisabled}
        label="Attach procedure"
        onChange={(value) => onProcedureChange(value ?? "NONE")}
        options={[
          { label: "None", value: "NONE" },
          ...procedures
            .filter((item) => item.status.toLowerCase() === "published")
            .map((item) => ({ label: item.name, value: item.id })),
        ]}
        placeholder={
          procedureStatus === "loading"
            ? "Loading procedures…"
            : procedureStatus === "error"
              ? "Unable to load procedures"
              : procedures.some(
                    (item) => item.status.toLowerCase() === "published",
                  )
                ? "Select a procedure"
                : "No published procedures available"
        }
        value={procedure}
      />
      {procedureStatus === "error" && onRetryProcedures ? (
        <Alert
          tone="negative"
          title="Procedures could not be loaded."
          action={
            <Button variant="outline" size="sm" onClick={onRetryProcedures}>
              Retry Procedures
            </Button>
          }
        />
      ) : null}
      {showVendor ? (
        <Input
          disabled={readonlyDetails}
          label="Vendor / contractor"
          placeholder="e.g. SKF Service, local bearing supplier..."
          value={vendor}
          onChange={(event) => onVendorChange(event.target.value)}
        />
      ) : null}
    </Stack>
  );
}
